import { useEffect, useState } from 'react';
import { listInvoicesResult, listPaymentsResult } from '../../services';
import { resolveAccountingDocumentLinks } from '../../services/deal-accounting-links';
import { listDocumentsResult } from '@/modules/documents/services';

const sameId = (left, right) => left != null && right != null && String(left) === String(right);

export function getPaymentPlanRows(payment, rows = []) {
  const related = rows.filter(row => payment.invoiceId != null
    ? sameId(row.invoiceId, payment.invoiceId)
    : payment.completedDealId != null ? sameId(row.completedDealId, payment.completedDealId)
      : payment.inProgressDealId != null ? sameId(row.inProgressDealId, payment.inProgressDealId)
        : sameId(row.id, payment.id));
  return [...new Map([...related, payment].map(row => [String(row.id), row])).values()]
    .sort((a, b) => (parseInt(a.installment, 10) || 1) - (parseInt(b.installment, 10) || 1)
      || String(a.dueDate || '').localeCompare(String(b.dueDate || '')));
}

export function usePaymentPlan({ open, payment, invoice, allPayments }) {
  const [state, setState] = useState({ id: null, loading: true, rows: [], invoice: null, error: '' });
  const { id, invoiceId, completedDealId, inProgressDealId } = payment;
  const hasPlan = invoiceId != null || completedDealId != null || inProgressDealId != null;
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setState({ id, loading: true, rows: [], invoice: null, error: '' });
    async function load() {
      try {
        const [paymentsResult, invoiceResult] = await Promise.all([
          allPayments !== undefined ? Promise.resolve({ ok: true, data: allPayments })
            : hasPlan ? listPaymentsResult() : Promise.resolve({ ok: true, data: [] }),
          invoice ? Promise.resolve({ ok: true, data: [invoice] })
            : invoiceId != null ? listInvoicesResult() : Promise.resolve({ ok: true, data: [] }),
        ]);
        if (!active) return;
        const linkedInvoice = (invoiceResult.data || []).find(item => sameId(item.id, invoiceId)) || null;
        const missingInvoice = invoiceId != null && !linkedInvoice;
        setState({ id, loading: false, rows: paymentsResult.ok ? paymentsResult.data : [], invoice: linkedInvoice,
          error: !paymentsResult.ok || !invoiceResult.ok || missingInvoice ? 'Piano rate o fattura non disponibili.' : '' });
      } catch {
        if (active) setState({ id, loading: false, rows: [], invoice: null, error: 'Impossibile caricare il piano rate.' });
      }
    }
    load();
    return () => { active = false; };
  }, [open, id, invoiceId, completedDealId, inProgressDealId, hasPlan, invoice, allPayments]);
  const current = state.id === id;
  return { loading: !current || state.loading, error: current ? state.error : '',
    invoice: invoice || (current ? state.invoice : null),
    rows: getPaymentPlanRows(payment, allPayments ?? (current ? state.rows : [])) };
}

export function usePaymentDocuments(open, payment) {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ id: null, loading: true, documents: [], error: '' });
  const { id, invoiceId, completedDealId, inProgressDealId } = payment;
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setState({ id, loading: true, documents: [], error: '' });
    async function load() {
      let error = '';
      let links = [{ linkedModule: 'ACCOUNTING', linkedEntityType: 'ACCOUNTING_PAYMENT', linkedEntityId: id }];
      try { links = await resolveAccountingDocumentLinks({ id, invoiceId, completedDealId, inProgressDealId }, 'ACCOUNTING_PAYMENT'); }
      catch (failure) { error = failure.message || 'Alcuni collegamenti agli allegati non sono disponibili.'; }
      const results = await Promise.allSettled(links.map(link => listDocumentsResult(link)));
      if (!active) return;
      const documents = [];
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value?.ok) documents.push(...(result.value.data?.documents || []));
        else error = result.status === 'fulfilled' ? result.value?.message || 'Alcuni allegati non sono disponibili.' : 'Impossibile caricare gli allegati.';
      });
      setState({ id, loading: false, documents: [...new Map(documents.map(document => [String(document.id), document])).values()], error });
    }
    load();
    return () => { active = false; };
  }, [open, id, invoiceId, completedDealId, inProgressDealId, revision]);
  return { ...(state.id === id ? state : { loading: true, documents: [], error: '' }), refresh: () => setRevision(value => value + 1) };
}
